import { Box, Grid } from '@mui/material';
import React, { Suspense, useMemo } from 'react';
import { useFullScreenHandle } from 'react-full-screen';
import { endDateFromTimePointType } from '../../../api/timeranges/utils';
import { useDashboardPageOptionsContext } from '../../../common/components/dashboards/contexts';
import { Skeleton } from '../../../common/components/Skeleton';
import { Months } from '../../../common/components/timeslider/types';
import { useManualRefetchCountHandle } from '../../../common/hooks';
import {
  useDisplayGlobalContext,
  useGlobalFilterContext,
  useGlobalLocaleContext,
  useGlobalSegmentationLevel1Context,
  useGlobalTimeSliderContext,
  useLatestPreferencesContext,
  useTimePointSelectorContext,
} from '../../../context/contexts';
import { format, getMomentDate, monthEnd, now } from '../../../date/date-utils';
import { StoryboardDateUtils } from '../../../settings/dashboard-configs/utils';
import { ApplicationsAtHiredFactbox } from './components/ApplicationsAtHiredFactbox';
import { AppsAndPassthroughFactbox } from './components/AppsAndPassthroughFactbox';
import { NewGradRecruitmentFunnelChart } from './components/NewGradRecruitmentFunnelChart';
import { NewGradRecruitmentMetricsTable } from './components/NewGradRecruitmentMetricsTable';
import { getNewGradRecruitmentTimeSelection } from './utils';

export const NewGradRecruitmentPage: React.FC = () => {
  const pageOptions = useDashboardPageOptionsContext();
  const domainPreferences = useLatestPreferencesContext();
  const [timePointSelectorState] = useTimePointSelectorContext();
  const firstMonthOfYear =
    domainPreferences.finYearStartMonth ?? Months.January;
  const dateFromTimeSlider = endDateFromTimePointType(
    timePointSelectorState.date,
    timePointSelectorState.selectedGranularity,
    firstMonthOfYear
  );
  const defaultReferenceDate = format(monthEnd(now()));
  const { timeOptions = { timeSliderEnabled: false } } = pageOptions;
  const { timeSliderEnabled } = timeOptions;
  const baseDateForPage = timeSliderEnabled
    ? dateFromTimeSlider
    : (timeOptions.referenceDate ?? defaultReferenceDate);
  const storyboardDateUtils = useMemo(
    () => new StoryboardDateUtils(getMomentDate(baseDateForPage).valueOf()),
    [baseDateForPage]
  );
  const timeSelection = useMemo(
    () => getNewGradRecruitmentTimeSelection(storyboardDateUtils),
    [storyboardDateUtils]
  );

  const manualDataRefetchCountHandle = useManualRefetchCountHandle();
  const {
    filterTray,
    timeOptions: pageTimeOptions,
    segmentationLevel1,
  } = pageOptions;
  const fullScreenHandle = useFullScreenHandle();
  const timeSliderHandle = useGlobalTimeSliderContext();
  const pageTimeSliderEnabled = pageTimeOptions?.timeSliderEnabled ?? true;
  const filterTrayEnabled = filterTray?.enabled ?? true;
  const segmentationLevel1Enabled = segmentationLevel1?.enabled ?? true;
  const [timeSliderState] = timeSliderHandle;
  const finalTimeSliderState = pageTimeSliderEnabled ? timeSliderState : null;
  const globalFilterHandle = useGlobalFilterContext();
  const [filtersState] = globalFilterHandle;
  const finalFiltersState = filterTrayEnabled
    ? filtersState
    : { ...filtersState, items: [] };
  const globalSegmentationHandle = useGlobalSegmentationLevel1Context();
  const [segmentationState] = globalSegmentationHandle;
  const finalSegmentationState = segmentationLevel1Enabled
    ? segmentationState
    : { ...segmentationState, items: [] };
  const displayGlobalHandle = useDisplayGlobalContext();
  const [displayGlobalState] = displayGlobalHandle;
  const locale = useGlobalLocaleContext();
  const preferences = useLatestPreferencesContext();

  const externalProps = {
    manualDataRefetchCountHandle,
    timeSliderState: finalTimeSliderState,
    filtersState: finalFiltersState,
    segmentationState: finalSegmentationState,
    preferences,
    displayGlobalState,
    fullScreenHandle,
    locale,
  };

  const globalPageFilter = `APPLICATION.CUSTOM_FILTER_1 = '新卒'`;
  return (
    <Box
      sx={{
        height: '100%',
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top row: Two factboxes */}
      <Grid
        container
        justifyContent="space-around"
        spacing={2}
        sx={{ height: '30%', minHeight: 0 }}
      >
        <Grid item xs={3} sx={{ minHeight: 0 }}>
          <Box sx={{ height: '100%' }}>
            <ApplicationsAtHiredFactbox
              timeSelection={timeSelection}
              externalProps={externalProps}
              filters={globalPageFilter}
            />
          </Box>
        </Grid>
        <Grid item xs={3} sx={{ minHeight: 0 }}>
          <Box sx={{ height: '100%' }}>
            <AppsAndPassthroughFactbox
              timeSelection={timeSelection}
              externalProps={externalProps}
              filters={globalPageFilter}
            />
          </Box>
        </Grid>
      </Grid>
      {/* Bottom row: Two charts */}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, mt: 2 }}>
        <Grid item xs={6} sx={{ minHeight: 0 }}>
          <Box sx={{ height: '100%' }}>
            <Suspense fallback={<Skeleton />}>
              <NewGradRecruitmentFunnelChart
                timeSelection={timeSelection}
                externalProps={externalProps}
              />
            </Suspense>
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ minHeight: 0 }}>
          <Box sx={{ height: '100%' }}>
            <Suspense fallback={<Skeleton />}>
              <NewGradRecruitmentMetricsTable
                timeSelection={timeSelection}
                externalProps={externalProps}
              />
            </Suspense>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
